import superagent from 'superagent'
import { tz } from 'moment-timezone'
import localForage from 'localforage'
import { setActiveOrg } from 'redux/modules/auth'
import { TOKEN_STATES } from 'constants/enums'
import Cookies from 'js-cookie'
import { generateHash } from 'utils/random'
import { get } from 'lodash'
import urlLib from 'url'

import config from 'config'
import { maskIds } from 'utils/url'
import activityMonitorStorage from 'helpers/activityMonitorStorage'

const baseUrl = config.baseUrl || ''
const methods = ['get', 'post', 'put', 'patch', 'del']

export function formatUrl(path, isExternal, { userType, organizationId, locale, data = {} } = {}) {
  if (path && path.indexOf('api/') === 0) {
    let apiPath = config.apiURL + '/' + path.slice(4)

    if (userType) {
      apiPath += `${apiPath.includes('?') ? '&' : '?'}userType=${userType}`
    }

    if (organizationId) {
      apiPath += `${apiPath.includes('?') ? '&' : '?'}organizationId=${organizationId}`
    }

    if (locale) {
      apiPath += `${apiPath.includes('?') ? '&' : '?'}locale=${locale}`
    }

    apiPath += `${apiPath.includes('?') ? '&' : '?'}timeZone=${tz.guess()}`

    __SERVER__ && console.log(`🔥  Calling API - ${path.slice(4)}`)
    return apiPath
  }

  if (path && path.indexOf('apiv2/') === 0) {
    let apiPath = config.apiv2URL + '/' + path.slice(6)

    let prefix = '?'

    if (userType) {
      apiPath += `${apiPath.includes('?') ? '&' : '?'}userType=${userType}`
    }


    if (locale) {
      if (apiPath.includes('?')) {
        prefix = '&'
      }
      apiPath = `${apiPath}${prefix}locale=${locale}`
    }

    !__SERVER__ && console.debug('Calling server...', apiPath.split('?')[0])
    return apiPath
  }

  if (isExternal) {
    return path
  }

  let adjustedPath = path[0] !== '/' ? '/' + path : path

  switch (adjustedPath) {
  case '/save-user':
    adjustedPath = '/save-user'
    break
  case '/save-user-bo':
    adjustedPath = '/save-user-bo'
    break
  case '/logout':
    adjustedPath = '/logout'
    break
  case '/tokens':
    adjustedPath = '/tokens'
    break
  case '/loadAuth':
    adjustedPath = '/loadAuth'
    break
  case '/mastercard-payment':
    adjustedPath = '/mastercard-payment'
    break
  case '/authenticate':
    adjustedPath = `/authenticate?id=${data.client_id}`
    break
  case '/authenticateBulk':
    adjustedPath = '/authenticateBulk'
    break
    case '/authenticate/mfa/verify':
      adjustedPath = '/authenticate/mfa/verify'
      break
  case '/download-image':
    adjustedPath = '/download-image'
    break
  case '/poe-translations':
    adjustedPath = `/poe-translations?locale=${locale}`
    break
  default:
    adjustedPath = config.apiURL + adjustedPath
    break
  }

  if (__SERVER__) {
    // Prepend host and port of the API server to the path.
    // console.log(
    //   'Calling from server',
    //   'http://' + config.host + ':' + config.port + adjustedPath
    // )
    return 'http://' + config.host + ':' + config.port + adjustedPath
  }

  // if (!__SERVER__) {
  //   console.log('Calling from client...', baseUrl + adjustedPath)
  // }
  return baseUrl + adjustedPath
}

class _ApiClient {
  constructor(req) {
    methods.forEach(
      method =>
        (this[method] = (
          path,
          {
            params,
            data,
            external,
            removeToken = false,
            formData = false,
            textResponse,
            unauthorizedAction = true
          } = {}
        ) => {
          const createPromise = () =>
            new Promise((resolve, reject) => {
              const request = superagent[method](
                formatUrl(path, external || textResponse, {
                  userType: get(data, 'userType') || this.userType,
                  organizationId: get(data, 'orgId') || this.organizationId,
                  locale: this.locale,
                  data
                })
              )

              const endpoint = get(request, 'url')
                ? method.toUpperCase() + ' ' + maskIds(urlLib.parse(get(request, 'url')).pathname)
                : undefined

              request.withCredentials()
              const requireJson = __CLIENT__ && !external && !textResponse

              activityMonitorStorage.resetLastActivity()

              this.unauthorizedAction = unauthorizedAction

              if (params) {
                request.query(params)
              }

              if (__SERVER__ && req.get('cookie')) {
                request.set('cookie', req.get('cookie'))
              }

              if (requireJson) {
                request.set('Accept', 'application/json')
              }

              if (this.token && !removeToken) {
                request.set('Authorization', 'Bearer ' + this.token)
              }

              if (this.organizationId && this.token && !removeToken) {
                request.query(`organizationId=${this.organizationId}`)
              }

              if (config.productionServer) {
                request.set('X-Frame-Options', 'DENY')
              }

              const requestId = generateHash(20)

              request.set('X-Request-ID', requestId)

              const isCSFRCheckNeeded = request.method !== "GET"
              const csfrToken = Cookies.get('csrfts')
              const csfrTokenORBg02 = Cookies.get('XSRF-TOKEN')

              if(isCSFRCheckNeeded){
                if(csfrToken && request.url.includes("v1")){
                  request.set('Csrf-Token', csfrToken)
                }

                if(csfrTokenORBg02 && request.url.includes("v2")){
                  request.set('Csrf-Token', csfrTokenORBg02)
                }
              }

              // Client identification = frontoffice
              request.query('c=f')

              if (formData) {
                const parts = [].concat(data)
                const multipartData = new FormData()

                parts.forEach(item => {
                  multipartData.append('file', item.blob, item.fileName)
                })

                request.send(multipartData)
              }

              if (data && !formData) {
                request.send(data)
              }

              request.end((err, res = {}) => {
                let responseObject = {}

                if (method === 'get') {
                  if (!textResponse) {
                    responseObject = res.body
                  } else {
                    responseObject = res.text
                  }
                } else {
                  responseObject = ''
                  if (res.text) {
                    try {
                      responseObject = JSON.parse(res.text)
                    } catch (e) {
                      responseObject = res.text
                      console.log('API CLIENT ERR', e)
                    }
                  }
                }

                if (
                  err &&
                  responseObject &&
                  (responseObject.errorCode === TOKEN_STATES.revoked.id ||
                    responseObject.errorCode === TOKEN_STATES.unauthorized.id) &&
                  typeof window !== 'undefined'
                ) {
                  window.location.replace(`${config.publicPath}/logout`)
                }

                err
                  ? reject(
                  {
                    body: responseObject,
                    headers: res.headers,
                    unauthorizedAction,
                    err,
                    requestId,
                    requireJson,
                    endpoint
                  } || err
                  )
                  : resolve({
                    body: responseObject,
                    headers: res.headers,
                    requestId,
                    requireJson,
                    endpoint
                  })
              })
            })

          if (!this.userType && !__SERVER__) {
            return localForage
              .getItem('activeOrganization')
              .then(org => {
                if (!org) {
                  return
                }
                this.userType = org.role
                this.organizationId = org.id
                this.dispatch(setActiveOrg(org))
                return
              })
              .catch(console.error)
              .then(createPromise)
          }
          return createPromise()
        })
    )
  }
}

const ApiClient = _ApiClient

export default ApiClient
